// @flow
import * as React from 'react';
import {
  AgentCurrentClientsMetricsFilterInput, AgentCurrentClientsMetricsOutput,
  PageInput
} from '@coverright/data-access/types/enrollment';
import { SearchInput, SelectInput } from '@coverright/ui/inputs';
import { MenuItem, Stack, Typography, Link as MuiLink, SxProps, Button } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { ComponentProps } from 'react';
import {
  DataGrid, DataGridColumn,
  ListPageWrapper,
  PageTitle,
  useAgentCurrentClientsMetricsQuery,
  useSessionStorageState
} from '../../shared';

const initialFilters: AgentCurrentClientsMetricsFilterInput = {searchTerm: '', agentEnabled: true};

export const SalesManager = () => {
  const [page, setPage] = usePage();
  const [filter, setFilter] = useFilter();

  const {data, loading, previousData} = useAgentCurrentClientsMetricsQuery({
    fetchPolicy: 'cache-and-network',
    variables: { filter }
  })

  const rows = data?.agentCurrentClientsMetrics || previousData?.agentCurrentClientsMetrics || [];

  return (
    <ListPageWrapper>
      <PageTitle title={'Agent Status Report'}>
        <Stack direction={'row'} gap={2}>
          <SearchInput value={filter.searchTerm || ''}
                       loading={loading}
                       onChange={searchTerm => setFilter(p => ({...p, searchTerm}))} />
          <SelectInput value={filter.agentEnabled ? 'enabled' : 'disabled'}
                       wrapperSx={{'& .MuiFormControl-root': {mt: 0}}}
                       hideTick
                       onChange={event => setFilter(p => ({...p, agentEnabled: event.target.value === 'enabled'}))}>
            <MenuItem value={'enabled'}>Enabled agents</MenuItem>
            <MenuItem value={'disabled'}>Disabled agents</MenuItem>
          </SelectInput>
        </Stack>
      </PageTitle>

      <DataGrid page={page.page}
                rowsPerPage={page.size}
                columns={columns}
                total={data?.agentCurrentClientsMetrics?.length || 0}
                loading={loading}
                onPageChange={p => setPage(prev => ({...prev, page: p }))}
                onRowsPerPageChange={p => setPage(prev => ({page: 0, size: p }))}
                rows={rows}/>
    </ListPageWrapper>
  );
};

const usePage = () => {
  return useSessionStorageState<PageInput>(
    'salesManagerPage',
    { page: 0, size: 20 }
  );
};

const useFilter = () => {
  return useSessionStorageState<AgentCurrentClientsMetricsFilterInput>(
    'salesManagerFilters',
    initialFilters
  );
};

const columns: DataGridColumn<AgentCurrentClientsMetricsOutput, Record<string, never>>[] = [
  {
    title: 'Name',
    value: agent => (
      <Typography variant={'body2'} fontWeight={500} py={.3}>
        {agent.agentName}
        <Typography variant={'caption'} color={'text.secondary'} sx={{display: 'block'}}>
          {agent.agentEmail}
        </Typography>
      </Typography>
    ),
  },
  {
    title: 'Today\'s Follow-Ups',
    value: agent => <Link to={`/dashboard/today-follow-ups/${agent.agentId}`}>{agent.followUpNotFuture}</Link>,
  },
  {
    title: 'Outdated Follow-Ups',
    value: agent => (
      <Link to={`/dashboard/outdated-follow-ups/${agent.agentId}`} sx={{color: agent.followUpOutdated ? 'error.500' : 'success.500'}}>
        {agent.followUpOutdated}
      </Link>
    ),
  },
  {
    title: 'Future Follow-Ups',
    value: agent => <Link to={`/dashboard/future-follow-ups/${agent.agentId}`}>{agent.followUpFuture}</Link>,
  },
  {
    title: 'Today\'s SQLs',
    value: agent => <Link to={`/dashboard/today-sql/${agent.agentId}`}>{agent.sqlNotFuture}</Link>,
  },
  {
    title: 'Outdated SQLs',
    value: agent => (
      <Link to={`/dashboard/outdated-sql/${agent.agentId}`} sx={{color: agent.sqlOutdated ? 'error.500' : 'success.500'}}>
        {agent.sqlOutdated}
      </Link>
    ),
  },
  {
    title: 'Future SQLs',
    value: agent => <Link to={`/dashboard/future-sql/${agent.agentId}`}>{agent.sqlUpFuture}</Link>,
  },
]

const Link = (props: ComponentProps<typeof RouterLink> & {sx?: SxProps}) => (
  <MuiLink sx={props.sx} component={RouterLink} to={props.to}>{props.children}</MuiLink>
);

